import React, { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { type MultiLevelMenuItem } from '@/common/types/multi-level-menu.type';
import { type AllProductsQueryParams } from '@/common/types/product.type';
import { type FilteredDataType } from '@/features/store/types/filter.type';
import { useRouter } from 'nextjs-toploader/app';
import { isDesktop } from 'react-device-detect';
import { Swiper, SwiperSlide } from 'swiper/react';
import Button from '@/common/components/button';
import Text from '@/common/components/text';
import { orderMock } from '@/common/constants/filter-product-list.constant';
import { DomainConfigManager } from '@/common/utils/domain-config-manager';
import CloseIcon from '~/public/assets/images/svg/close-line.svg';
import FilterIcon from '~/public/assets/images/svg/filter-icon.svg';
import Filter from './filter.component';
import ProductSearch from './product-search.component';

export interface Props {
  payload: AllProductsQueryParams;
  onSearch: (query: string) => void;
  filteredData: FilteredDataType;
  setFilteredData: React.Dispatch<React.SetStateAction<FilteredDataType>>;
  allCategories: MultiLevelMenuItem[];
}

// eslint-disable-next-line max-lines-per-function
export default function FilterTags({
  payload,
  filteredData,
  setFilteredData,
  allCategories,
  onSearch,
}: Props) {
  const router = useRouter();
  const vendorPathname = usePathname();
  const searchParams = useSearchParams();
  const queryParams = searchParams.toString();
  const [isClientReady, setIsClientReady] = useState(false);
  const [isTenant, setIsTenant] = useState<boolean | null>(null);

  useEffect(() => {
    let mounted = true;
    setIsClientReady(true);
    DomainConfigManager.isTenant().then((result) => {
      if (mounted) setIsTenant(result);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const removeFilter = (filter: MultiLevelMenuItem) => {
    const newCategories = filteredData.category.filter(
      (item) => item.value !== filter.value
    );
    setCategory(newCategories);
  };

  const addFilter = (filter: MultiLevelMenuItem) => {
    const newCategories = [...filteredData.category, filter];
    setCategory(newCategories);
  };

  const handleClick = (filter: MultiLevelMenuItem) => {
    const isFilterActive = filteredData.category.some(
      (item) => item.value === filter.value
    );

    isFilterActive ? removeFilter(filter) : addFilter(filter);
  };

  const setCategory = (filterCategories: MultiLevelMenuItem[]) => {
    setFilteredData({ ...filteredData, category: filterCategories });

    const newCategoryValue = filterCategories
      .map((item) => item.value)
      .join('-');

    const searchParams = new URLSearchParams(queryParams);
    searchParams.set('page', '1');

    if (filterCategories.length > 0) {
      searchParams.set('category', newCategoryValue);
    } else {
      searchParams.delete('category');
    }

    router.replace(`${vendorPathname}?${searchParams.toString()}`, {
      scroll: false,
    });
  };

  const removeIsDiscountedOrIsAvailable = (target: string) => {
    setFilteredData({
      ...filteredData,
      [target]: false,
    });
    let restOfParams = '';
    if (target === 'isDiscounted') {
      restOfParams = queryParams.replace(/isDiscounted=[^&]*&?/, '');
    } else {
      restOfParams = queryParams.replace(/isAvailable=[^&]*&?/, '');
    }
    router.replace(`${vendorPathname}?${restOfParams}`, { scroll: false });
  };

  const removeSortFilter = () => {
    setFilteredData({
      ...filteredData,
      ...{ orderBy: { value: '', label: '' } },
    });
    let restOfParams = '';
    restOfParams = queryParams.replace(/orderBy=[^&]*&?/, '');
    router.replace(`${vendorPathname}?${restOfParams}`, { scroll: false });
  };

  const CloseIconComponent = () => {
    return <CloseIcon className="mr-1 inline-block h-5 w-5 text-black-100" />;
  };

  return (
    <>
      <div className="mt-2 w-full  lg:!mb-[40px]  lg:mt-0">
        <div className="flex w-full flex-row justify-between gap-10">
          <div className="flex w-full flex-col gap-y-5 ">
            <Text className="text-l lg:text-xl font-semibold text-black-400">
              محصولات
            </Text>

            {isClientReady ? (
              <div className="flex w-full grow flex-row items-center justify-start gap-[8px] overflow-auto">
                <div className="flex w-full flex-row items-center justify-start lg:pb-1">
                  <Filter
                    categoryList={allCategories}
                    filteredData={filteredData}
                    setFilteredData={setFilteredData}
                  >
                    {isDesktop ? (
                      <Button
                        className=" h-11 w-11 !rounded-lg !border !border-solid !border-[#EDECEB] !bg-[#F2F1F1]  !p-[6px] ml-2"
                        type="filterTag"
                      >
                        <FilterIcon className="mx-auto inline-block" />
                      </Button>
                    ) : null}
                  </Filter>
                  <div
                    className={`hidden w-full ${isTenant ? '' : 'max-w-[710px]'} cursor-grab lg:block`}
                  >
                    {isDesktop && allCategories?.length > 0 ? (
                      <Swiper spaceBetween={11} slidesPerView={'auto'} freeMode>
                        {filteredData.orderBy.value.length > 0 && (
                          <SwiperSlide className="!w-fit">
                            <Button
                              type="black"
                              onClick={() => {
                                removeSortFilter();
                              }}
                              className="!text-white-default mr-[8px] w-fit flex-shrink-0 !rounded-lg !border !border-solid !border-[#EDECEB] !bg-[#191308] !p-[6px] !px-[8px] !py-[10px] text-sm font-medium"
                            >
                              <>
                                {
                                  orderMock.find(
                                    (sortItem) =>
                                      sortItem.value ===
                                      filteredData.orderBy?.value
                                  )?.label
                                }
                              </>
                              <CloseIconComponent />
                            </Button>
                          </SwiperSlide>
                        )}
                        {filteredData.isDiscounted && (
                          <SwiperSlide className="!w-fit">
                            <Button
                              type="black"
                              onClick={() => {
                                removeIsDiscountedOrIsAvailable('isDiscounted');
                              }}
                              className={
                                '!text-white-default w-fit !rounded-lg !border !border-solid !border-[#EDECEB] !bg-[#191308] !p-[8px] text-sm font-medium'
                              }
                            >
                              دارای تخفیف
                              <CloseIconComponent />
                            </Button>
                          </SwiperSlide>
                        )}
                        {filteredData.isAvailable && (
                          <SwiperSlide className="!w-fit">
                            <Button
                              type="black"
                              onClick={() => {
                                removeIsDiscountedOrIsAvailable('isAvailable');
                              }}
                              className={
                                '!text-white-default w-fit !rounded-lg !border !border-solid !border-[#EDECEB] !bg-[#191308] !p-[8px] text-sm font-medium'
                              }
                            >
                              فقط محصولات موجود
                              <CloseIconComponent />
                            </Button>
                          </SwiperSlide>
                        )}
                        {allCategories
                          .filter((item) => {
                            const selected = filteredData.category.find(
                              (category) => item.value === category.value
                            );
                            return (
                              (selected && filteredData.category.length > 0) ||
                              filteredData.category.length === 0
                            );
                          })
                          .map((item) => {
                            const selected = filteredData.category.find(
                              (category) => item.value === category.value
                            );
                            return (
                              <SwiperSlide key={item.value} className="!w-fit">
                                <Button
                                  onClick={() => {
                                    handleClick(item);
                                  }}
                                  type={selected ? 'black' : 'filterTag'}
                                  className={
                                    'w-fit !rounded-lg !border !border-solid !border-[#EDECEB] !p-[8px] text-sm font-medium'
                                  }
                                  icon={selected && <CloseIconComponent />}
                                  iconPosition="end"
                                >
                                  {item.label}
                                </Button>
                              </SwiperSlide>
                            );
                          })}
                      </Swiper>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          {isTenant === false && (
            <ProductSearch
              className="hidden min-w-[370px] lg:flex lg:flex-col lg:px-1 lg:justify-end lg:!my-0"
              onSearch={onSearch}
            />
          )}
        </div>
      </div>
    </>
  );
}
